/**
 * RegisterAgentModal Component
 * Modal for registering a new Agent (Linux or Windows) using UID from installation
 * Dark theme matching SYLink platform design
 */

import React, { useState } from 'react';
import { X, Copy, Check, AlertCircle, Loader2, Download } from 'lucide-react';
import { registerAgent, downloadConfiguredInstaller } from '../../api/agents';
import { useTranslation } from 'react-i18next';

const RegisterAgentModal = ({ isOpen, onClose, onSuccess, workspaceId }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    uid: '',
    name: '',
    hostname: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.uid.trim()) {
      newErrors.uid = t('agents.register.uidRequired', 'L\'UID de l\'agent est requis');
    } else if (!/^agent_[a-f0-9]{12,32}$/.test(formData.uid.trim())) {
      newErrors.uid = t('agents.register.uidInvalid', 'Format UID invalide (doit \u00eatre: agent_[hex chars])');
    }
    if (!formData.name.trim()) {
      newErrors.name = t('agents.register.nameRequired', 'Le nom de l\'agent est requis');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('agents.register.nameMinLength', 'Le nom doit contenir au moins 3 caract\u00e8res');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const result = await registerAgent({
        uid: formData.uid.trim(),
        hostname: formData.hostname.trim() || null,
        name: formData.name.trim(),
        workspace_id: workspaceId || null
      });
      setRegistrationResult(result);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors de l\'enregistrement';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copySecret = () => {
    if (registrationResult?.hmac_secret) {
      navigator.clipboard.writeText(registrationResult.hmac_secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const handleFinalClose = () => {
    setFormData({ uid: '', name: '', hostname: '' });
    setErrors({});
    setRegistrationResult(null);
    setCopiedSecret(false);
    if (onSuccess && registrationResult) {
      onSuccess(registrationResult);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
          onClick={!registrationResult ? handleFinalClose : undefined}
        />

        {/* Modal */}
        <div className="relative bg-brand-black-light rounded-lg border border-gray-800 shadow-2xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">
              {registrationResult ? t('agents.register.registered', 'Agent Enregistr\u00e9') : t('agents.register.title', 'Enregistrer un Agent')}
            </h2>
            <button
              onClick={handleFinalClose}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {!registrationResult ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* UID Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    UID de l'Agent <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="uid"
                    value={formData.uid}
                    onChange={handleChange}
                    placeholder="agent_a1b2c3d4e5f6"
                    className={`w-full px-3 py-2 bg-[#060606] border rounded-lg text-sm text-white placeholder-gray-500 font-mono focus:outline-none focus:ring-1 transition-colors ${
                      errors.uid
                        ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-gray-700 focus:border-brand-green/50 focus:ring-brand-green/20'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.uid && (
                    <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.uid}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {t('agents.register.uidHintLinux', 'L\'UID affiché par la commande sylink-agent setup')}
                  </p>
                </div>

                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('agents.register.name', 'Nom de l\'Agent')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Agent Comptabilit\u00e9"
                    className={`w-full px-3 py-2 bg-[#060606] border rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                      errors.name
                        ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-gray-700 focus:border-brand-green/50 focus:ring-brand-green/20'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Hostname Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Hostname <span className="text-gray-500 text-xs font-normal">({t('common.optional', 'optionnel')})</span>
                  </label>
                  <input
                    type="text"
                    name="hostname"
                    value={formData.hostname}
                    onChange={handleChange}
                    placeholder="srv-comptabilite"
                    className="w-full px-3 py-2 bg-[#060606] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-green/50 focus:ring-1 focus:ring-brand-green/20 transition-colors"
                    disabled={isSubmitting}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('agents.register.hostnameAuto', 'D\u00e9tect\u00e9 automatiquement si vide')}
                  </p>
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {errors.submit}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleFinalClose}
                    className="flex-1 px-4 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-brand-black-lighter transition-colors text-sm"
                    disabled={isSubmitting}
                  >
                    {t('common.cancel', 'Annuler')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-brand-green text-brand-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('agents.register.registering', 'Enregistrement...')}
                      </>
                    ) : (
                      t('agents.register.submit', 'Enregistrer l\'Agent')
                    )}
                  </button>
                </div>
              </form>
            ) : (
              /* Success screen */
              <div className="space-y-5">
                {/* Success Message */}
                <div className="p-4 bg-brand-green/10 border border-brand-green/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-brand-green">{t('agents.register.success', 'Agent enregistr\u00e9 avec succ\u00e8s')}</h3>
                      <p className="text-sm text-gray-400 mt-1">
                        L'agent "{registrationResult.name}" a \u00e9t\u00e9 enregistr\u00e9 dans le syst\u00e8me.
                      </p>
                    </div>
                  </div>
                </div>

                {/* HMAC Secret */}
                <div className="p-4 bg-orange-500/10 border-2 border-orange-500/40 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-orange-400">IMPORTANT - Secret HMAC</h3>
                      <p className="text-sm text-gray-400 mt-1 mb-3">
                        {t('agents.register.secretOnce', 'Ce secret ne sera affich\u00e9 qu\'une seule fois. Copiez-le maintenant.')}
                      </p>
                      <div className="bg-[#060606] p-3 rounded-lg border border-orange-500/30 font-mono text-sm text-white break-all">
                        {registrationResult.hmac_secret}
                      </div>
                      <button
                        onClick={copySecret}
                        className="mt-3 px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-lg hover:bg-orange-500/30 transition-colors flex items-center gap-2 text-sm"
                      >
                        {copiedSecret ? (
                          <>
                            <Check className="w-4 h-4" />
                            {t('common.copied', 'Copi\u00e9 !')}
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            {t('agents.register.copySecret', 'Copier le Secret')}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Download Pre-configured Installer */}
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <h4 className="font-medium text-purple-400 text-sm mb-2">
                    {t('agents.register.downloadConfigured', 'T\u00e9l\u00e9charger l\'installateur pr\u00e9-configur\u00e9')}
                  </h4>
                  <p className="text-sm text-gray-400 mb-3">
                    {t('agents.register.downloadConfiguredDesc', 'Ce ZIP contient l\'agent avec le fichier de configuration pr\u00e9-rempli (UID + secret). D\u00e9compressez et lancez \u2014 aucune configuration manuelle n\u00e9cessaire.')}
                  </p>
                  <button
                    onClick={() => downloadConfiguredInstaller(registrationResult.agent_id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    {t('agents.register.downloadConfiguredBtn', 'T\u00e9l\u00e9charger l\'installateur configur\u00e9')}
                  </button>
                </div>

                {/* Manual Instructions (alternative) */}
                <details className="text-sm">
                  <summary className="font-medium text-gray-400 cursor-pointer hover:text-white transition-colors">
                    {t('agents.register.manualConfig', 'Configuration manuelle (alternative)')}
                  </summary>
                  <ol className="space-y-2 text-gray-400 mt-3 pl-1">
                    {[
                      t('agents.register.step_copy', 'Copiez le secret HMAC ci-dessus'),
                      <>{t('agents.register.step_open', 'Sur la machine cible, ouvrez')} : <code className="px-1.5 py-0.5 bg-[#060606] border border-gray-700 rounded text-xs font-mono text-gray-300">appsettings.json</code></>,
                      <>{t('agents.register.step_fill', 'Renseignez')} <code className="px-1.5 py-0.5 bg-[#060606] border border-gray-700 rounded text-xs font-mono text-gray-300">"hmac_secret"</code> {t('common.and', 'et')} <code className="px-1.5 py-0.5 bg-[#060606] border border-gray-700 rounded text-xs font-mono text-gray-300">"agent_uid"</code></>,
                      <>{t('agents.register.step_run', 'Lancez')} : <code className="px-1.5 py-0.5 bg-[#060606] border border-gray-700 rounded text-xs font-mono text-gray-300">sylink-agent run</code></>,
                    ].map((text, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-green/10 text-brand-green text-xs font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ol>
                </details>

                {/* Close Button */}
                <div className="pt-3 border-t border-gray-800">
                  <button
                    onClick={handleFinalClose}
                    className="w-full px-4 py-2 bg-brand-green text-brand-black font-semibold rounded-lg hover:opacity-90 transition-opacity text-sm"
                  >
                    {t('common.close', 'Fermer')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterAgentModal;
